<script setup lang="ts">
import { ref, computed } from 'vue'
import { MessageSquarePlus, X, Send, Mic, MicOff, ImagePlus, Loader2 } from 'lucide-vue-next'
import { supabase } from '@/supabase'
import { currentOrgId } from '@/composables/useOrgSettings'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

const open = ref(false)
const message = ref('')
const images = ref<File[]>([])
const imagePreviews = ref<string[]>([])
const submitting = ref(false)
const submitted = ref(false)
const error = ref('')

// Voice recording
const recording = ref(false)
const audioBlob = ref<Blob | null>(null)
const audioUrl = ref('')
let mediaRecorder: MediaRecorder | null = null
let audioChunks: BlobPart[] = []

async function startRecording() {
  error.value = ''
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    audioChunks = []
    mediaRecorder = new MediaRecorder(stream)
    mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.push(e.data) }
    mediaRecorder.onstop = () => {
      const blob = new Blob(audioChunks, { type: 'audio/webm' })
      audioBlob.value = blob
      audioUrl.value = URL.createObjectURL(blob)
      stream.getTracks().forEach(t => t.stop())
    }
    mediaRecorder.start()
    recording.value = true
  } catch {
    error.value = 'Microphone access denied.'
  }
}

function stopRecording() {
  mediaRecorder?.stop()
  recording.value = false
}

function clearAudio() {
  audioBlob.value = null
  audioUrl.value = ''
}

function onImagePick(e: Event) {
  const files = (e.target as HTMLInputElement).files
  if (!files) return
  for (const f of Array.from(files)) {
    if (images.value.length >= 3) break
    images.value.push(f)
    imagePreviews.value.push(URL.createObjectURL(f))
  }
  ;(e.target as HTMLInputElement).value = ''
}

function removeImage(i: number) {
  images.value.splice(i, 1)
  imagePreviews.value.splice(i, 1)
}

const canSubmit = computed(() => !submitting.value && (message.value.trim() || images.value.length || audioBlob.value))

async function submit() {
  if (!canSubmit.value) return
  submitting.value = true
  error.value = ''
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const uid = session?.user.id
    const email = session?.user.email

    // Upload images
    const uploadedImageUrls: string[] = []
    for (const img of images.value) {
      const ext = img.name.split('.').pop() || 'jpg'
      const path = `${uid}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
      const { error: upErr } = await supabase.storage.from('feedback-attachments').upload(path, img)
      if (upErr) throw upErr
      uploadedImageUrls.push(path)
    }

    // Upload audio
    let uploadedAudioUrl: string | null = null
    if (audioBlob.value) {
      const path = `${uid}/${Date.now()}-voice.webm`
      const { error: upErr } = await supabase.storage.from('feedback-attachments').upload(path, audioBlob.value)
      if (upErr) throw upErr
      uploadedAudioUrl = path
    }

    const { error: insertErr } = await supabase.from('feedback').insert({
      user_id: uid,
      user_email: email,
      page_url: window.location.pathname,
      message: message.value.trim() || null,
      image_urls: uploadedImageUrls,
      audio_url: uploadedAudioUrl,
      org_id: currentOrgId(),
    })
    if (insertErr) throw insertErr

    submitted.value = true
    setTimeout(() => {
      open.value = false
      submitted.value = false
      message.value = ''
      images.value = []
      imagePreviews.value = []
      audioBlob.value = null
      audioUrl.value = ''
    }, 1800)
  } catch (e: any) {
    error.value = e?.message || 'Failed to submit feedback.'
  } finally {
    submitting.value = false
  }
}

function toggle() {
  open.value = !open.value
  if (!open.value) {
    error.value = ''
    submitted.value = false
  }
}
</script>

<template>
  <!-- Floating bubble -->
  <div class="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
    <!-- Panel -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-95"
    >
      <div
        v-if="open"
        class="bg-background border rounded-xl shadow-xl w-80 p-4 flex flex-col gap-3"
        style="origin: bottom right"
      >
        <div class="flex items-center justify-between">
          <span class="font-semibold text-sm">Send feedback</span>
          <button @click="toggle" class="text-muted-foreground hover:text-foreground transition-colors">
            <X class="size-4" />
          </button>
        </div>

        <div v-if="submitted" class="text-center py-4 text-sm text-green-600 font-medium">
          Thanks for your feedback! ✓
        </div>

        <template v-else>
          <Textarea
            v-model="message"
            placeholder="Describe an issue or share feedback…"
            class="resize-none text-sm min-h-[80px]"
          />

          <!-- Image previews -->
          <div v-if="imagePreviews.length" class="flex gap-2 flex-wrap">
            <div
              v-for="(src, i) in imagePreviews"
              :key="i"
              class="relative size-16 rounded-md overflow-hidden border"
            >
              <img :src="src" class="object-cover size-full" />
              <button
                @click="removeImage(i)"
                class="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full size-4 flex items-center justify-center"
              >
                <X class="size-2.5" />
              </button>
            </div>
          </div>

          <!-- Audio preview -->
          <div v-if="audioUrl" class="flex items-center gap-2">
            <audio :src="audioUrl" controls class="h-8 flex-1" />
            <button @click="clearAudio" class="text-muted-foreground hover:text-destructive transition-colors">
              <X class="size-4" />
            </button>
          </div>

          <div v-if="error" class="text-destructive text-xs">{{ error }}</div>

          <!-- Actions row -->
          <div class="flex items-center gap-2">
            <!-- Image attach -->
            <label v-if="images.length < 3" class="cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
              <ImagePlus class="size-5" />
              <input type="file" accept="image/*" multiple class="sr-only" @change="onImagePick" />
            </label>

            <!-- Voice -->
            <button
              v-if="!audioUrl"
              @click="recording ? stopRecording() : startRecording()"
              :class="recording ? 'text-destructive animate-pulse' : 'text-muted-foreground hover:text-foreground'"
              class="transition-colors"
            >
              <component :is="recording ? MicOff : Mic" class="size-5" />
            </button>

            <div class="flex-1" />

            <Button size="sm" :disabled="!canSubmit" @click="submit" class="gap-1.5">
              <Loader2 v-if="submitting" class="size-3.5 animate-spin" />
              <Send v-else class="size-3.5" />
              Send
            </Button>
          </div>
        </template>
      </div>
    </Transition>

    <!-- Trigger button -->
    <button
      @click="toggle"
      class="bg-primary text-primary-foreground size-12 rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-all active:scale-95"
      aria-label="Feedback"
    >
      <X v-if="open" class="size-5" />
      <MessageSquarePlus v-else class="size-5" />
    </button>
  </div>
</template>
